import { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import { useChartTheme } from "@/lib/chartTheme";
import { registerCharts } from "./register";

registerCharts();

export interface PieSlice {
  label: string;
  value: number;
  color?: string;
}

interface PieChartProps {
  data: PieSlice[];
  /** Render as a doughnut (default) or solid pie. */
  variant?: "doughnut" | "pie";
  showLegend?: boolean;
  /** Optional centre label (only meaningful with doughnut). */
  centerLabel?: { value: string; sub?: string };
  height?: number;
  className?: string;
  /** Delay (ms) before the entry animation starts. */
  delay?: number;
}

export function PieChart({
  data,
  variant = "doughnut",
  showLegend = true,
  centerLabel,
  height = 240,
  className,
  delay = 0,
}: PieChartProps) {
  const { tokens, palette } = useChartTheme();

  const chartData = useMemo<ChartData<"doughnut">>(() => {
    return {
      labels: data.map((d) => d.label),
      datasets: [
        {
          data: data.map((d) => d.value),
          backgroundColor: data.map((d, i) => d.color ?? palette[i % palette.length]),
          // Border colour matches the card surface so slices read as separated
          // without a heavy ring. 1px stays subtle in light + dark.
          borderColor: tokens.surface,
          borderWidth: 1,
          hoverOffset: 8,
          hoverBorderWidth: 1,
        },
      ],
    };
  }, [data, tokens, palette]);

  // Pie / doughnut don't use Cartesian scales — set a fresh options object
  // (no `mergeOptions(base, ...)`) so inherited grid/tick config can't leak.
  const options = useMemo<ChartOptions<"doughnut">>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: variant === "doughnut" ? "68%" : 0,
    layout: { padding: 4 },
    plugins: {
      legend: {
        display: showLegend,
        position: "right",
        align: "center",
        labels: {
          color: tokens.fgMuted,
          font: { family: tokens.fontSans, size: 12 },
          usePointStyle: true,
          boxWidth: 6,
          boxHeight: 6,
          padding: 12,
        },
      },
      tooltip: {
        backgroundColor: tokens.fg,
        titleColor: tokens.surface,
        bodyColor: tokens.surface,
        borderWidth: 0,
        padding: 10,
        cornerRadius: 8,
        boxPadding: 6,
        usePointStyle: true,
        displayColors: true,
        titleFont: { family: tokens.fontSans, weight: 600, size: 12 },
        bodyFont:  { family: tokens.fontMono, weight: 500, size: 12 },
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000,
      easing: "easeOutQuart",
      delay,
    },
  }), [variant, showLegend, tokens, delay]);

  return (
    <div className={className} style={{ height, position: "relative" }}>
      <Doughnut data={chartData} options={options} />
      {centerLabel && variant === "doughnut" && (
        <div
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
          style={{ paddingRight: showLegend ? "35%" : 0 }}
        >
          <div className="text-2xl font-semibold tracking-tight text-fg tabular">
            {centerLabel.value}
          </div>
          {centerLabel.sub && (
            <div className="mt-0.5 text-xs text-fg-muted">{centerLabel.sub}</div>
          )}
        </div>
      )}
    </div>
  );
}
